import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import {
  acceptPartnerInvitation,
  getPartnerInvitation,
  type PartnerInvitationData,
} from '../services/userService';

const PartnerInvitationPage: React.FC = () => {
  const { tenderId } = useParams<{ tenderId: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { account } = useAccount();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invitation, setInvitation] = useState<PartnerInvitationData | null>(null);
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!tenderId || !account) {
        return;
      }
      try {
        const response = await getPartnerInvitation(tenderId);
        if (response.status === 'already_accepted') {
          showSnackbar('You have already responded to this invitation.', 'info');
          navigate('/dashboard');
          return;
        }
        if (response.success && response.invitation) {
          setInvitation(response.invitation);
        } else {
          showSnackbar('Failed to load invitation details.', 'error');
          navigate('/dashboard');
        }
      } catch (err) {
        const error = err as Error;
        showSnackbar(error.message || 'Error fetching invitation.', 'error');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchInvitation();
  }, [tenderId, account, navigate, showSnackbar]);

  const handleAction = async (accept: boolean) => {
    if (!tenderId) {
      return;
    }
    setProcessing(true);
    try {
      await acceptPartnerInvitation(tenderId, accept);
      showSnackbar(
        accept
          ? 'Invitation accepted! The Tender Owner will now review and grant you secure access.'
          : 'Invitation declined.',
        'success'
      );
      navigate('/dashboard');
    } catch (err) {
      const error = err as Error;
      showSnackbar(error.message || 'Error processing invitation.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <PageContainer title="Partner Invitation">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (!invitation) {
    return null; // Will redirect in useEffect
  }

  return (
    <PageContainer title="Partner Invitation">
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          <Typography variant="h4" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
            Partnership Invitation
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 4 }}>
            <strong>{invitation.inviterName}</strong> has invited you to collaborate on an upcoming
            tender opportunity.
          </Typography>

          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 3,
              borderRadius: 2,
              mb: 4,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" gutterBottom color="primary">
              Inviter Contact Information
            </Typography>
            <Typography
              variant="body1"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
            >
              <BusinessIcon fontSize="small" color="action" /> <strong>Company:</strong>{' '}
              {invitation.inviterName}
            </Typography>
            <Typography
              variant="body1"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
            >
              <EmailIcon fontSize="small" color="action" /> <strong>Email:</strong>{' '}
              <Link href={`mailto:${invitation.inviterEmail}`}>{invitation.inviterEmail}</Link>
            </Typography>
            {invitation.inviterPhone && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <PhoneIcon fontSize="small" color="action" /> <strong>Phone:</strong>{' '}
                <Link href={`tel:${invitation.inviterPhone}`}>{invitation.inviterPhone}</Link>
              </Typography>
            )}
            {invitation.inviterWebsite && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <LanguageIcon fontSize="small" color="action" /> <strong>Website:</strong>{' '}
                <Link href={invitation.inviterWebsite} target="_blank" rel="noopener noreferrer">
                  {invitation.inviterWebsite}
                </Link>
              </Typography>
            )}
            {invitation.inviterPartnerId && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Want to learn more about the inviting company?{' '}
                  <Link
                    component={RouterLink}
                    to={`/partner-details/${invitation.inviterPartnerId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View their Partner Profile
                  </Link>
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ bgcolor: 'action.hover', p: 3, borderRadius: 2, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Tender Details
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Title:</strong> {invitation.title}
            </Typography>
            <Typography variant="body1">
              <strong>Scope:</strong> {invitation.description || 'No description provided.'}
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Why You Were Selected
            </Typography>
            <Typography variant="body1" paragraph>
              The BIDLOGIQ platform identified your company,{' '}
              <strong>{invitation.partnerName}</strong>, as a strong fit for this tender with a
              capability match score of <strong>{invitation.matchScore}%</strong>.
            </Typography>
            <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
              <strong>Your Value Add:</strong> {invitation.valueAdd}
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            If you accept this invitation, you will be granted access to the tender workspace where
            you can collaborate, share documents, and work together on the submission.
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              size="large"
              disabled={processing}
              onClick={() => handleAction(false)}
              sx={{ px: 4, fontWeight: 'bold' }}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={processing}
              onClick={() => handleAction(true)}
              sx={{ px: 4, fontWeight: 'bold' }}
            >
              {processing ? <CircularProgress size={24} color="inherit" /> : 'Accept & Join'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </PageContainer>
  );
};

export default PartnerInvitationPage;
